/**
 * 画布素材数据转换工具。
 *
 * 上传服务、素材库列表和节点创建使用的对象结构不完全一致，这里统一转换成
 * `CanvasAsset`，避免各个入口重复拼字段或使用不同 ID 规则。
 */
import {
  type CanvasAsset,
  type CanvasAssetType,
} from '../model/canvas-asset-types';

interface UploadResultLike {
  materialId?: number | string;
  taskId?: string;
  url: string;
  name?: string;
  type: string;
  size?: number;
  width?: number;
  height?: number;
  duration?: number;
  mimeType?: string;
}

interface MaterialLike {
  id?: number | string;
  material_id?: number | string;
  title?: string;
  name?: string;
  type: string;
  originalUrl?: string;
  previewUrl?: string;
  url?: string;
  cover?: string;
  thumb_1024?: string;
  width?: number;
  height?: number;
  duration?: number;
  size?: number;
  mimeType?: string;
  mime_type?: string;
  check_status?: number;
}

interface TaskOutputLike {
  material_id?: number | string;
  url?: string;
  thumb?: string;
  cover?: string;
  text?: string;
  width?: number;
  height?: number;
  duration?: number;
  size?: number;
  check_status?: number;
}

/**
 * 读取稳定素材 ID。
 *
 * @param value 候选 ID，支持字符串和数字
 * @param fallback 候选 ID 不可用时的兜底值
 * @returns 字符串形式的素材 ID
 */
function readAssetId(value: unknown, fallback: string): string {
  if (typeof value === 'string' && value) {
    return value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return fallback;
}

/**
 * 将外部素材类型收敛为 CanvasAssetType。
 *
 * @param value 上传或素材库返回的类型字符串
 * @returns 画布素材类型；调用方入口已经按媒体类型过滤，这里只做类型收窄
 */
function readCanvasAssetType(value: string): CanvasAssetType {
  return value as CanvasAssetType;
}

function readPositiveNumber(value: unknown): number | undefined {
  const numberValue = typeof value === 'number'
    ? value
    : typeof value === 'string' && value.trim() !== ''
      ? Number(value)
      : NaN;

  return Number.isFinite(numberValue) && numberValue > 0
    ? numberValue
    : undefined;
}

/**
 * 将上传结果转换为画布素材。
 *
 * @param result uploadAsset 返回的结果，要求至少包含 url、type 和可选 materialId
 * @returns 可写入 yAssetsData 的 CanvasAsset
 */
export function mapUploadResultToCanvasAsset(result: UploadResultLike): CanvasAsset {
  return {
    id: readAssetId(result.materialId, result.taskId ?? result.url),
    type: readCanvasAssetType(result.type),
    title: result.name ?? '未命名素材',
    url: result.url,
    width: readPositiveNumber(result.width),
    height: readPositiveNumber(result.height),
    duration: readPositiveNumber(result.duration),
    size: readPositiveNumber(result.size),
    mimeType: result.mimeType,
  };
}

/**
 * 将素材库/拖拽素材对象转换为画布素材。
 *
 * @param material 素材库条目或节点创建 asset 输入，支持 material_id/id、url/originalUrl 等字段
 * @returns 可写入 yAssetsData 的 CanvasAsset
 */
export function mapMaterialToCanvasAsset(
  material: MaterialLike,
): CanvasAsset {
  const url = material.originalUrl ?? material.url ?? '';
  const previewUrl = material.previewUrl ?? material.thumb_1024 ?? material.cover ?? url;
  const id = readAssetId(material.material_id, readAssetId(material.id, url || previewUrl));

  return {
    id,
    type: readCanvasAssetType(material.type),
    title: material.title ?? material.name ?? '未命名素材',
    url,
    previewUrl: previewUrl === url ? undefined : previewUrl,
    width: readPositiveNumber(material.width),
    height: readPositiveNumber(material.height),
    duration: readPositiveNumber(material.duration),
    size: readPositiveNumber(material.size),
    mimeType: material.mimeType ?? material.mime_type,
    check_status: material.check_status,
  };
}

/**
 * 将 AI 任务 output 转换为画布素材。
 *
 * @param output 后端任务 outputs[0][0] 返回的结果对象，要求包含 material_id 和 url
 * @param type 当前节点媒体类型，用于写入 CanvasAsset.type
 * @param title 素材标题，通常使用节点标题或任务提示词
 * @returns 可写入 yAssetsData 的 CanvasAsset；缺少 material_id/url 时返回 null
 *
 * 任务结果必须使用后端返回的 material_id 作为稳定 assetId，避免把 url 拼进
 * assetRefs 导致 ID 过长且不可追踪。
 */
export function mapTaskOutputToCanvasAsset(
  output: TaskOutputLike | undefined,
  type: CanvasAssetType,
  title = '任务结果',
): CanvasAsset | null {
  if (!output?.material_id || (!output.url && !output.text)) {
    return null;
  }

  const previewUrl = output.thumb ?? output.cover;

  return {
    id: readAssetId(output.material_id, ''),
    type,
    title,
    url: output.url || '',
    previewUrl,
    text: output.text,
    width: readPositiveNumber(Number(output.width)),
    height: readPositiveNumber(Number(output.height)),
    duration: readPositiveNumber(Number(output.duration)),
    size: readPositiveNumber(Number(output.size)),
    check_status: output.check_status,
  };
}
